import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useEmail } from '../../contexts/EmailContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiCopy, FiEye, FiSave, FiX } from 'react-icons/fi';


const EmailTemplateEditor = ({ template, onClose }) => {
  const { createTemplate, updateTemplate } = useEmail();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'initial_outreach',
    subject: '',
    content: '',
    variables: []
  });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        category: template.category || 'initial_outreach',
        subject: template.subject || '',
        content: template.content || '',
        variables: template.variables || []
      });
    }
  }, [template]);

  const categories = [
    { value: 'initial_outreach', label: 'Initial Outreach' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'nurturing', label: 'Nurturing' },
    { value: 'meeting_invitation', label: 'Meeting Invitation' },
    { value: 'reengagement', label: 'Re-engagement' },
    { value: 'onboarding', label: 'Onboarding' }
  ];

  const defaultTemplates = {
    initial_outreach: {
      subject: 'Exciting Opportunity for Experienced Life Insurance Professionals',
      content: `Hi {{firstName}},

I hope this email finds you well. I'm reaching out to you because of your impressive background in life insurance with {{company}}.

We're Prosperity Leaders, and we specialize in helping top-performing agents like yourself significantly increase their earning potential while maintaining the client relationships they've worked so hard to build.

Here's what makes us different:

• 40-60% income increases in the first year for most agents
• Commission rates starting at 85% and going up to 95%
• Comprehensive training and ongoing support
• Advanced lead generation systems
• No upfront costs or franchise fees

Given your {{experience}} of experience and your current production level, I believe you'd be an excellent fit for our opportunity.

Would you be interested in a brief 15-minute call this week to learn more about how we can help you take your career to the next level?

I have some time available on {{availableDay}} or {{alternativeDay}}. Which works better for you?

Looking forward to hearing from you.

Best regards,
{{senderName}}
{{senderTitle}}
Prosperity Leaders

P.S. Many of our top agents wish they had made this move sooner. Don't let another year pass wondering "what if."`
    },
    follow_up: {
      subject: 'Following up on our conversation about your career growth',
      content: `Hi {{firstName}},

I wanted to follow up on my previous email about the opportunity with Prosperity Leaders.

I know you're busy building your business with {{company}}, but I didn't want you to miss out on learning about how our top agents are achieving remarkable growth.

Quick reminder of what we offer:
• Proven system that's helped agents increase income by 40-60%
• Higher commission rates (85-95% vs industry standard)
• Comprehensive training and support
• Advanced lead systems
• No territorial restrictions

Since you have {{experience}} of experience in life insurance, you already have the foundation for success with our system.

I have a few time slots available this week for a brief call:
• {{timeSlot1}}
• {{timeSlot2}}
• {{timeSlot3}}

Would any of these work for you? The call will only take 15 minutes, and I guarantee you'll learn something valuable about growing your business.

Best regards,
{{senderName}}`
    },
    meeting_invitation: {
      subject: 'Invitation: Exclusive Overview Meeting - Prosperity Leaders Opportunity',
      content: `Hi {{firstName}},

Thank you for your interest in learning more about the Prosperity Leaders opportunity.

I'd like to invite you to an exclusive overview meeting where you'll discover:

• Exactly how our system works and why it's so effective
• Real case studies of agents who've made the transition
• Detailed compensation breakdown and earning potential
• The support and training you'll receive
• Next steps if you decide to move forward

Meeting Details:
📅 Date: {{meetingDate}}
⏰ Time: {{meetingTime}}
🔗 Zoom Link: {{zoomLink}}
📞 Dial-in: {{dialIn}}

What to expect:
• 45-minute presentation with Q&A
• No pressure, just information
• Opportunity to speak with current agents
• Clear next steps if interested

Please confirm your attendance by replying to this email. If the scheduled time doesn't work, let me know and we can find an alternative.

I'm excited to share this opportunity with you and answer any questions you might have.

Best regards,
{{senderName}}
{{senderTitle}}
Prosperity Leaders

P.S. Space is limited, so please confirm as soon as possible.`
    },
    nurturing: {
      subject: 'Industry insights that could impact your business',
      content: `Hi {{firstName}},

I hope you're having a great week at {{company}}.

I wanted to share some interesting industry insights that might be relevant to your business:

📊 Recent Industry Trends:
• Life insurance sales are up 15% this quarter
• Consumer demand for financial protection is at an all-time high
• Top agents are leveraging new technology to scale their businesses

This growth presents incredible opportunities for experienced agents like yourself who understand the market and have strong client relationships.

At Prosperity Leaders, we've been helping agents capitalize on these trends through:
• Advanced lead generation systems
• Proven sales methodologies
• Higher commission structures
• Comprehensive training programs

Many agents in your area have already made the transition and are seeing remarkable results. For example:
• Sarah J. increased her income by 65% in her first year
• Michael R. doubled his production within 18 months
• Jennifer L. built a team of 12 agents and now earns six figures

Would you be interested in learning more about how these agents achieved such success?

I'd be happy to share more details in a brief call. No pressure, just information that could help you grow your business regardless of your current situation.

Best regards,
{{senderName}}

P.S. If you're curious about the specific strategies these agents used, I can share a detailed case study during our call.`
    },
    reengagement: {
      subject: 'New developments in our opportunity - Worth another look?',
      content: `Hi {{firstName}},

It's been a while since we last spoke about the opportunity with Prosperity Leaders. I hope your business with {{company}} is going well.

I'm reaching out because we've made some exciting developments that I thought might interest you:

🆕 What's New:
• Enhanced commission structure (now up to 95%)
• New lead generation technology
• Expanded territory options
• Additional training resources
• Simplified onboarding process

Since our last conversation, we've helped over 200 agents make successful transitions, with an average income increase of 52% in their first year.

I understand timing is everything in this business, and what wasn't right before might be perfect now.

Would you be open to a brief 10-minute call to hear about these new developments? No commitment required - just sharing information that might be valuable for your career.

I have some availability:
• {{timeOption1}}
• {{timeOption2}}
• {{timeOption3}}

If none of these work, just let me know what's convenient for you.

Best regards,
{{senderName}}

P.S. Even if you're not interested in making a change right now, you might know other agents who could benefit from this opportunity. We offer referral bonuses for successful introductions.`
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.content) {
      toast.error('Name, subject, and content are required');
      return;
    }

    if (template) {
      updateTemplate(template.id, formData);
      toast.success('Template updated successfully');
    } else {
      createTemplate(formData);
      toast.success('Template created successfully');
    }

    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const loadDefaultTemplate = () => {
    const defaultTemplate = defaultTemplates[formData.category];
    if (defaultTemplate) {
      setFormData(prev => ({
        ...prev,
        subject: defaultTemplate.subject,
        content: defaultTemplate.content
      }));
      toast.success('Default template loaded');
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('content');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    setFormData(prev => ({
      ...prev,
      content: before + variable + after
    }));
  };

  const commonVariables = [
    '{{firstName}}',
    '{{lastName}}',
    '{{company}}',
    '{{title}}',
    '{{experience}}',
    '{{location}}',
    '{{currentProduction}}',
    '{{senderName}}',
    '{{senderTitle}}',
    '{{availableDay}}',
    '{{alternativeDay}}',
    '{{timeSlot1}}',
    '{{timeSlot2}}',
    '{{timeSlot3}}',
    '{{meetingDate}}',
    '{{meetingTime}}',
    '{{zoomLink}}'
  ];

  const renderPreview = () => {
    let previewContent = formData.content;
    
    // Replace variables with sample data for preview
    const sampleData = {
      '{{firstName}}': 'John',
      '{{lastName}}': 'Smith',
      '{{company}}': 'State Farm Insurance',
      '{{title}}': 'Insurance Agent',
      '{{experience}}': '5 years',
      '{{location}}': 'Dallas, TX',
      '{{currentProduction}}': '$200K annually',
      '{{senderName}}': 'Sarah Johnson',
      '{{senderTitle}}': 'Regional Director',
      '{{availableDay}}': 'Tuesday',
      '{{alternativeDay}}': 'Thursday',
      '{{timeSlot1}}': 'Tuesday 2:00 PM',
      '{{timeSlot2}}': 'Wednesday 10:00 AM',
      '{{timeSlot3}}': 'Friday 3:00 PM',
      '{{meetingDate}}': 'March 15, 2024',
      '{{meetingTime}}': '2:00 PM EST',
      '{{zoomLink}}': 'https://zoom.us/j/123456789'
    };

    Object.entries(sampleData).forEach(([variable, value]) => {
      previewContent = previewContent.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return previewContent;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Email Template' : 'Create Email Template'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiEye} className="text-sm" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Editor */}
          <div className={`${showPreview ? 'w-1/2' : 'w-full'} overflow-y-auto border-r border-gray-200`}>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Initial Outreach - Life Insurance Agents"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief description of when to use this template"
                />
              </div>

              {/* Load Default Template */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-blue-900">Quick Start</h4>
                  <p className="text-sm text-blue-700">Load a pre-written template for this category</p>
                </div>
                <button
                  type="button"
                  onClick={loadDefaultTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Load Default
                </button>
              </div>

              {/* Subject Line */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter compelling subject line"
                />
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personalization Variables
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Click to insert variables into your content:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {commonVariables.map(variable => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertVariable(variable)}
                        className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors font-mono"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content *
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  required
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="Write your email content here. Use variables like {{firstName}} for personalization."
                />
              </div>
            </form>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="w-1/2 overflow-y-auto bg-gray-50">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                
                {/* Email Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">From:</span>
                      <span className="text-sm font-medium">Sarah Johnson &lt;sarah@prosperitybuilders.com&gt;</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">To:</span>
                      <span className="text-sm font-medium">John Smith &lt;john.smith@statefarm.com&gt;</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Subject:</span>
                      <span className="text-sm font-medium">{formData.subject}</span>
                    </div>
                  </div>
                  
                  {/* Email Body */}
                  <div className="p-4">
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                        {renderPreview()}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Copy Preview Button */}
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(renderPreview());
                    toast.success('Preview copied to clipboard');
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiCopy} className="text-sm" />
                  Copy Preview
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end items-center gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            {template ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailTemplateEditor;